import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./App.css";
import AutoExpandingTextArea from "./Components/AutoExpandingTextArea.tsx";
import iconCaret from "./photos/caret.svg";
import iconPlus from "./photos/plus.svg";
import { charCountColor } from "./utils/charCountColor.ts";

interface AIRoomInstructionsProps {
  roomName: string;
  sessionId: string;
  socket: any;
  setAIRoomState: (state: string) => void;
  showInfoMessage: (
    type: string,
    message: string,
    isAdd: boolean,
    duration?: string,
    isDelay?: boolean,
  ) => void;
  containerHeight: number;
  isEditProfile?: boolean;
}

interface Preset {
  preview: string;
  text: string;
}

const PRESETS: Preset[] = [
  { preview: "Legg inn insidejokes", text: "Insidejokes i gruppa: " },
  { preview: "Legg inn hemmeligheter om andre", text: "Hemmeligheter: " },
  { preview: "Legg inn stereotypier i rommet", text: "Stereotypier: " },
  { preview: "Legg inn hvem som liker hvem", text: "Hvem liker hvem: " },
  { preview: "Legg inn hvem som trenger å bli full", text: "Trenger å bli full: " },
  { preview: "Legg inn hvem som har beef", text: "Beef: " },
];

function AIRoomInstructions({
  roomName,
  sessionId,
  socket,
  setAIRoomState,
  showInfoMessage,
  containerHeight,
  isEditProfile = false,
}: AIRoomInstructionsProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [presetsOpen, setPresetsOpen] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const nameTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();
  const [loaded, setLoaded] = useState(false);

  const [hasExistingInstructions, setHasExistingInstructions] = useState(false);

  // Load existing instructions (and name if editing) on mount
  useEffect(() => {
    const handler = (data: any) => {
      if (!loaded && data.instructions !== undefined) {
        setInput(data.instructions || "");
        setHasExistingInstructions(data.hasSubmittedInstructions || false);
        if (data.name) setNameInput(data.name);
        setLoaded(true);
      }
    };
    socket.on("ai room your state", handler);
    socket.emit("ai room reconnect", { roomName, sessionId });
    return () => {
      socket.off("ai room your state", handler);
    };
  }, [socket, roomName, sessionId, loaded]);

  const sendDebounced = useCallback(
    (text: string) => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        socket.emit("ai room update instructions", {
          roomName,
          sessionId,
          instructions: text,
        });
      }, 500);
    },
    [socket, roomName, sessionId],
  );

  const submit = () => {
    clearTimeout(debounceRef.current);
    if (isEditProfile && nameInput.trim()) {
      socket.emit("ai room update name", {
        roomName,
        sessionId,
        name: nameInput.trim(),
      });
    }
    socket.emit("ai room submit instructions", {
      roomName,
      sessionId,
      instructions: input,
    });
    setAIRoomState("lobby");
  };

  const loadPreset = (text: string) => {
    const newText = input ? input + "\n" + text : text;
    setInput(newText);
    sendDebounced(newText);
    setPresetsOpen(false);
    setTimeout(() => {
      const tx = textAreaRef.current;
      if (!tx) return;
      tx.focus();
      tx.setSelectionRange(newText.length, newText.length);
      tx.scrollTop = tx.scrollHeight;
    }, 100);
  };

  return (
    <div className="App-header">
      <div className="container-row lift-topbar">
        <div className="container-row-start">
          <div
            className="button-tertiary-m button-text-m text-color-black"
            onClick={() =>
              setAIRoomState(
                isEditProfile || hasExistingInstructions
                  ? "lobby"
                  : "name-entry",
              )
            }
            style={{ maxHeight: "32.5px" }}
          >
            {t("back")}
          </div>
        </div>
        <div className="text-h3 text-color-black left-margin-s text-force-break">
          {roomName}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
          width: "100%",
        }}
      >
        {isEditProfile && (
          <div style={{ marginTop: 32, marginBottom: 16 }}>
            <p
              className="text-h2 text-color-black"
              style={{ marginBottom: 12 }}
            >
              NAVN
            </p>
            <AutoExpandingTextArea
              value={nameInput}
              onChange={setNameInput}
              textareaRef={nameTextAreaRef}
              placeholder={"SKRIV NAVN..."}
              maxLength={20}
            />
          </div>
        )}

        <p
          className="text-h2 text-color-black"
          style={{
            marginTop: isEditProfile ? 8 : 32,
            marginBottom: 24,
          }}
        >
          HEMMELIGE INSTRUKSER TIL AI
          {input.length > 0 && (
            <span
              style={{ color: charCountColor(input.length), marginLeft: 12 }}
            >
              {input.length}
            </span>
          )}
        </p>

        <AutoExpandingTextArea
          value={input}
          onChange={(text) => {
            setInput(text);
            sendDebounced(text);
          }}
          textareaRef={textAreaRef}
          placeholder={"SKRIV HER ..."}
          maxLength={500}
        />


        {/* Expandable presets accordion */}
        <div style={{ marginTop: 16 }}>
          <div
            className="container-row"
            style={{ alignItems: "center", cursor: "pointer" }}
            onClick={() => setPresetsOpen(!presetsOpen)}
          >
            <div className="container-row-start">
              <div className="text-h2">EKSEMPLER</div>
              <img
                style={{
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  rotate: "180deg",
                }}
                src={iconCaret}
                alt=""
                className={
                  presetsOpen
                    ? "rotate top-margin-s"
                    : "rotate-back top-margin-s"
                }
              />
            </div>
          </div>

          {presetsOpen && (
            <div
              style={{
                maxHeight: containerHeight - 400,
                overflow: "hidden",
                overflowY: "scroll",
              }}
              className="recommendation-slide-in"
            >
              {PRESETS.map((preset, index) => (
                <div
                  key={index}
                  className="list-item-container divider"
                  onClick={() => loadPreset(preset.text)}
                >
                  <div className="text-h4">{preset.preview}</div>
                  <img
                    style={{ paddingTop: "3px" }}
                    src={iconPlus}
                    alt=""
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
        <div
          className="button-primary black-fill button-text-l text-color-pink shadow-pink box-button-center"
          onClick={submit}
        >
          {isEditProfile ? "LAGRE" : input.trim() ? "NESTE" : "HOPP OVER"}
        </div>
      </div>
    </div>
  );
}

export default AIRoomInstructions;
